import { useDashboardTeam } from '../../hooks/useDashboardTeam'
import { Activity, AlertTriangle, Building2, Headphones, Users } from 'lucide-react'
import { KpiCard, KpiRow } from '../patterns/KpiCard'
import { SectionCard } from '../patterns/SectionCard'
import DashboardFreshness from './DashboardFreshness'

export default function TeamDashboard() {
  const { data, loading, refresh } = useDashboardTeam()
  if (loading) return <div className="flex-1 p-8"><div className="h-24 rounded-xl bg-card animate-pulse" /></div>
  if (data?.forbidden) return (
    <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
      Team &amp; Clients is available to managers only.
    </div>
  )
  if (!data) return null
  const dot = (s: string) => (s === 'high' ? 'bg-rose-500' : 'bg-amber-500')
  return (
    <div className="flex-1 min-w-0 h-full flex flex-col bg-background text-foreground">
      <div className="h-14 shrink-0 px-6 flex items-center gap-3 border-b border-border bg-card">
        <h1 className="text-sm font-semibold text-foreground">Team &amp; Clients</h1>
        <div className="ml-auto">
          <DashboardFreshness generatedAt={data.generatedAt} onRefresh={refresh} />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <main className="px-8 py-6 space-y-6">
          {/* Presence strip — live online + occupied audio channels */}
          <div className="rounded-xl bg-card ring-1 ring-border px-4 py-3 flex items-center gap-4 text-sm flex-wrap">
            <span className="flex items-center gap-1.5 font-semibold"><Users size={15} className="text-emerald-400" /> {data.presence.online} online</span>
            <span className="flex items-center gap-1.5 text-muted-foreground"><Headphones size={14} /> In audio:</span>
            {data.presence.audioChannels.length === 0 && <span className="text-muted-foreground/70 text-xs">none</span>}
            {data.presence.audioChannels.map((c: any) => (
              <span key={c.channelId} className="text-xs px-2 py-0.5 rounded-full bg-muted text-foreground/80">{c.name} · {c.count}</span>
            ))}
          </div>

          {/* KPI row */}
          <KpiRow>
            {data.kpis.map((k: any) => (
              <KpiCard key={k.label} label={k.label} value={k.value} />
            ))}
          </KpiRow>

          {/* Clients needing attention + team activity */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <SectionCard icon={<AlertTriangle size={14} className="text-rose-400" />} title="Clients needing attention" noBodyPadding>
              <ul className="divide-y divide-border/60">
                {data.clientsAtRisk.length === 0 && <li className="px-5 py-6 text-center text-xs text-muted-foreground/70">All clear.</li>}
                {data.clientsAtRisk.map((c: any) => (
                  <li key={c.id} className="px-5 py-3 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot(c.severity)}`} />
                    <span className="text-sm text-foreground flex-1 truncate">{c.name}</span>
                    <span className="text-[11px] text-muted-foreground truncate">{c.reason}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>
            <SectionCard icon={<Activity size={14} className="text-blue-400" />} title="Team activity" noBodyPadding>
              <ul className="divide-y divide-border/60">
                {data.feed.length === 0 && <li className="px-5 py-6 text-center text-xs text-muted-foreground/70">Nothing yet.</li>}
                {data.feed.map((f: any) => (<li key={f.id} className="px-5 py-2.5 text-[13px] text-foreground/90 truncate">{f.text}</li>))}
              </ul>
            </SectionCard>
          </div>

          {/* Commitments + open topics, company-wide */}
          <div className="rounded-xl bg-card ring-1 ring-border px-4 py-3 flex items-center gap-2 text-sm">
            <Building2 size={14} className="text-muted-foreground" />
            <span className="text-foreground font-medium">By client:</span>
            <span className="text-muted-foreground">{data.commitmentsOpen} commitments · {data.openTopics} open topics</span>
          </div>
        </main>
      </div>
    </div>
  )
}
